import { useCallback, useMemo, useState } from "react";
import { roundToCents } from "@common/helpers/monetary";
import { MatchableItem, MatchableTransaction } from "../types";
import {
    createRemainingLine,
    RemainingLine,
    remainingLineToMatchableItem,
    resolveRemainingLineDefaults,
} from "./useRemainingLines";
import { useRemainingLineSetters } from "./useRemainingLineSetters";
import {
    TransactionLedgerContext,
    TransactionLedgerContexts,
} from "./transactionLedgerContexts";

export interface TransactionRemainingLine extends RemainingLine {
    transactionId: number;
}

interface Params {
    transactions: MatchableTransaction[];
    contextsByTransactionId: TransactionLedgerContexts;
}

function buildLine({
    transaction,
    amount,
    context,
}: {
    transaction: MatchableTransaction;
    amount: number;
    context: TransactionLedgerContext;
}): TransactionRemainingLine {
    return {
        ...createRemainingLine({
            sourceTransaction: transaction,
            ...resolveRemainingLineDefaults({
                transaction,
                entityId: context.entityId,
                accounts: context.accounts,
            }),
            amount,
        }),
        transactionId: transaction.id,
    };
}

export function useMultiRemainingLines({
    transactions,
    contextsByTransactionId,
}: Params) {
    const [lines, setLines] = useState<TransactionRemainingLine[]>([]);

    const transactionsById = useMemo(
        () => new Map(transactions.map((tx) => [tx.id, tx])),
        [transactions],
    );

    const show = useCallback(
        (
            differences: ReadonlyArray<{
                transactionId: number;
                amount: number;
            }>,
        ) => {
            setLines((prev) =>
                prev.length > 0
                    ? prev
                    : differences.flatMap(({ transactionId, amount }) => {
                          const transaction =
                              transactionsById.get(transactionId);
                          const context =
                              contextsByTransactionId.get(transactionId);
                          return transaction &&
                              context?.hasAccrualLedger &&
                              amount > 0
                              ? [buildLine({ transaction, amount, context })]
                              : [];
                      }),
            );
        },
        [contextsByTransactionId, transactionsById],
    );

    const setters = useRemainingLineSetters(setLines);

    const addLine = useCallback(
        (transactionId: number) => {
            const transaction = transactionsById.get(transactionId);
            const context = contextsByTransactionId.get(transactionId);
            if (!transaction || !context?.hasAccrualLedger) {
                return;
            }
            setLines((prev) => [
                ...prev,
                buildLine({ transaction, amount: 0, context }),
            ]);
        },
        [contextsByTransactionId, transactionsById],
    );

    const removeLine = useCallback((id: string) => {
        setLines((prev) => prev.filter((line) => line.id !== id));
    }, []);

    const totalsByTransaction = useMemo(() => {
        const totals: Record<number, number> = {};
        for (const line of lines) {
            if (line.accrualAccountId) {
                totals[line.transactionId] = roundToCents(
                    (totals[line.transactionId] ?? 0) + line.amount,
                );
            }
        }
        return totals;
    }, [lines]);

    const items = useMemo<MatchableItem[]>(
        () =>
            lines.flatMap((line) => {
                const transaction = transactionsById.get(line.transactionId);
                return transaction
                    ? [
                          remainingLineToMatchableItem({
                              line,
                              sourceTransaction: transaction,
                              financialAccountId:
                                  transaction.financialAccount?.id ?? null,
                              ledgerEntityId: contextsByTransactionId.get(
                                  line.transactionId,
                              )?.entityId,
                          }),
                      ]
                    : [];
            }),
        [lines, transactionsById, contextsByTransactionId],
    );

    return {
        lines,
        items,
        isVisible: lines.length > 0,
        totalsByTransaction,
        show,
        addLine,
        removeLine,
        ...setters,
    };
}
